import { json, jsonParseLinter } from '@codemirror/lang-json'
import { linter, lintGutter } from '@codemirror/lint'
import styled from '@emotion/styled'
import CodeMirror from '@uiw/react-codemirror'
import { useMemo, useRef } from 'react'
import { Controller, FieldPath, FieldValues, PathValue, UseControllerProps, useFormContext } from 'react-hook-form'
import { WithClassName } from '~/types/utils'

type JSONEditorInputProps<T extends FieldValues, U extends FieldPath<T>> = WithClassName & {
  indentationSize?: number
  disabled?: boolean
  jsonSyntaxValidation?: (v: PathValue<T, U>) => string | boolean
  onLintErrors?: (errors: { message: string; from: number; to: number }[]) => void
  syncLintErrorsWithForm?: boolean
} & UseControllerProps<T, U>

const BaseJSONEditor = <T extends { jsonValue: string }, U extends FieldPath<T>>({
  name,
  control,
  rules,
  shouldUnregister,
  disabled = false,
  syncLintErrorsWithForm = true,
  ...props
}: JSONEditorInputProps<T, U>) => {
  const { setError, clearErrors } = useFormContext()
  const previousErrorRef = useRef<string | null>(null)

  const jsonLintExtension = useMemo(() => {
    const jsonLinter = jsonParseLinter()

    return linter(
      (view) => {
        const diagnostics = jsonLinter(view)

        const errors = diagnostics.map(({ message, from, to }) => ({
          message,
          from,
          to,
        }))

        if (syncLintErrorsWithForm) {
          const currentError = errors[0]?.message ?? null

          if (currentError !== previousErrorRef.current) {
            previousErrorRef.current = currentError

            if (currentError) {
              setError(name, {
                type: 'lint',
                message: currentError,
              })
            } else {
              clearErrors(name)
            }
          }
        }

        return diagnostics
      },
      { delay: 150 }
    )
  }, [name, setError, clearErrors, syncLintErrorsWithForm, previousErrorRef])

  return (
    <Controller
      control={control}
      name={name}
      rules={{
        validate: {
          ...rules?.validate,
        },
        ...rules,
      }}
      shouldUnregister={shouldUnregister}
      render={({ field: { value, onChange } }) => {
        return (
          <CodeMirror
            {...props}
            value={value}
            extensions={[json(), jsonLintExtension, lintGutter()]}
            readOnly={disabled}
            editable={!disabled}
            onChange={(val) => {
              onChange(val)
            }}
            theme={disabled ? 'light' : undefined}
          />
        )
      }}
    />
  )
}

export const JSONEditorInput = styled(BaseJSONEditor)``
